import {isNegativeNumber, isPositiveNumber, isZero, parseInteger, parseNumber} from '@benchling/js-utils';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as biojs from 'BioJS';
import {Notation} from 'biojs/BioConstants';
import {NucleotideType} from 'biojs/Util';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import {SchemaSummary} from 'constants/RegistryConstants';
import {GridType} from 'constants/SampleConstants';
import {OLIGO_MAX_LENGTH} from 'constants/SequenceConstants';
import {Status} from 'constants/StatusConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {EntitySelectionMode} from 'inventory/InventoryConstants';
import {getRestrictionStatusFromCaseInsensitiveLabel} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {canApplyPlateLayout} from 'lab-automation/settings/automation-input-file-config/PlateLayoutCellDropdown';
import * as metrics from 'metrics/Metrics';
import {isFullUTFlexibilityEnabled} from 'modbio/util';
import StringResolutionManager from 'models/managers/StringResolutionManager';
import {tryParse, tryParseDatetimeAsDate} from 'notebook/data-tables/DatetimeFields';
import {isAmountQs} from 'notebook/integrations/registry/MixtureGroupConstants';
import {OptionCellDropdownOption} from 'notebook/plain-tables/selectors/OptionCellDropdown';
import {LOADING} from 'notebook/registration-tables/RegistrationTableColumnDefinitions';
import {
  AntibodyDomainColumnDefinition,
  ColumnDefinition,
  ComplexPolymerComponentColumnDefinition,
  SchemaFieldColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {parseTopologyFromString} from 'notebook/structured-tables/columns/TopologyColumnParsers';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import tableTypeToKind from 'notebook/structured-tables/util/tableTypeToKind';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  BatchResolvedValue,
  BioentityResolvedValue,
  CustomResolutionQueryType,
  CustomResolvedValue,
  DropdownResolvedValue,
  EntryResolvedValue,
  PartyResolvedValue,
  ProcessTaskResolvedValue,
  StorableResolvedValue,
  StringResolutionQuery,
  StringResolutionType,
  StringResolvedValue,
  UserResolvedValue,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  ArrayTypedValue,
  BaseTypedValueType,
  TableColumnType,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {isPrimitiveMultiFieldType} from 'notebook/tables/typed-values/components/editors/ArrayTypedValueInput';
import {
  canParseTypedValueFromString,
  coerceTypedValueToType,
  convertTagToTypedValue,
  formatTypedValue,
  getBioentityInfosFromTypedValue,
  getLinksFromTypedValue,
  getLinksKeyFromTypedValue,
  getTypedValueFromBioentitySummary,
  getUserTypedValueFromEntitySummary,
  isTypedValueEmpty,
  parseTextAsFloat,
  resolveTypedValueToBoolean,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {ContainersColumnMode} from 'notebook/transfer-tables/TransferTableConstants';
import {getPMEIsForDocument} from 'procedures/worksheets/ProcedurePMEIDocumentStore';
import {getStore} from 'redux-stores/Store';
import {ParentStorableType} from 'samples/storables/StorableSchemaConstants';
import StorableStore from 'samples/StorableStore';
import {
  BatchLinkSchemaField,
  BioentityLinkSchemaField,
  EntryLinkSchemaField,
  FIELD_TYPES,
  FieldType,
  SchemaField,
  StorableLinkSchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {isTagSchema} from 'schemas/SchemaPredicates';
import {getInitialTag} from 'schemas/tags/TagUtil';
import {getFieldTypeName} from 'tag-schema/util/TagSchemaUtil';
import APIUtil from 'util/APIUtil';
import {ellipsize} from 'util/NameUtil';
import {removeCarriageReturn} from 'util/removeCarriageReturn';
import {assertNever, Optional} from 'util/Types';
import {resolveStageRunValue} from 'workflows/util/StageRunUtil';

import {ResolutionQuery} from '../excess/Resolver';
import {ResolutionQueryType} from '../TableStoreTransforms';
import {
  BioentityLinkSchemaFieldTypedValue,
  BioentityTypedValue,
  LibraryParameterDefinitionTypedValue,
  PartiesTypedValue,
  ProcedureConditionReplicatesTypedValue,
  ProcedureTypedValue,
  ProcessTaskTypedValue,
  StorableInfo,
  StorablesTypedValue,
  StringTypedValue,
  StudiesTypedValue,
  UnitReferenceTypedValue,
  UserTypedValue,
} from './TypedValueTypes';

export interface ResolutionContext {
  tableType: Optional<string>;
  hasFormula: boolean;
  fileId?: string;
}

export interface ResolvedValueInfo {
  resolvedValue: TypedValue;
  error: ExcessUtil.CellError | null;
}

function shouldResolveText(text: string): boolean {
  return Boolean(text && text !== LOADING);
}

/**
 * Gets the file summary from the redux store. This is a brittle pattern and we should avoid replicating it.
 *
 * TODO(BNCH-18458): make independent of the Redux store and the live schemaField
 *
 * @deprecated
 */
function getFileSummary(id: string): BioentitySummary {
  return getStore().getState().models.fileSummariesById[id];
}

function shouldTypedValueBeResolvedAsync(fieldType: FieldType, isTimezoneNamePresent: boolean): boolean {
  // If timezone is present date/datetime values will be resolved async to allow the async importing of moment-timezone
  return (
    isTimezoneNamePresent && (fieldType === FIELD_TYPES.DATE.type || fieldType === FIELD_TYPES.DATETIME.type)
  );
}

function resolveTypedValueToSelectorOptions(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  schemaField: SchemaField
): ResolvedValueInfo {
  const text = formatTypedValue(typedValue, {});
  if (!shouldResolveText(text)) {
    return {resolvedValue: typedValue, error: null};
  }

  const makeTypedValueFromResolvedValue = function (resolvedValue: DropdownResolvedValue): TypedValue {
    // TODO(BNCH-18458): remove dependency on live schemaField
    const tagValue = getInitialTag(schemaField);
    tagValue.value = resolvedValue.value;
    tagValue.schemaFieldSelectorOptionIds = resolvedValue.schemaFieldSelectorOptionIds;
    return convertTagToTypedValue(tagValue);
  };

  const resolutionQuery = makeDropdownResolutionQuery(text, schemaField.apiId, schemaField.isMulti);
  const resolution = resolveFromResolutionQuery(typedValue, resolutionQuery, makeTypedValueFromResolvedValue);

  if (resolution.error) {
    const alternateResolutionQuery = makeDropdownResolutionQuery(
      text,
      schemaField.apiId,
      !schemaField.isMulti
    );
    const alternateResolution = resolveFromResolutionQuery(
      typedValue,
      alternateResolutionQuery,
      makeTypedValueFromResolvedValue
    );
    if (alternateResolution.error) {
      return resolution;
    }
    resolution.error = new ExcessUtil.ValueError('Cannot support multiple links in a single link field.');
  }

  return resolution;
}

export function makeUserTypedValueFromResolvedValue(
  resolvedValue: UserResolvedValue
): UserTypedValue | StringTypedValue {
  assert(resolvedValue.userHandles.length === 1, 'Single user is expected.');
  const handle = resolvedValue.userHandles[0];
  const {models: modelsInState} = getStore().getState();
  const entitySummary = modelsInState.managed.entitySummariesByHandle?.[handle];

  return entitySummary != null
    ? getUserTypedValueFromEntitySummary(entitySummary)
    : {
        type: BaseTypedValueType.STRING,
        rawValue: handle,
      };
}

function resolveTypedValueToUser(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  formattedValue: string
): ResolvedValueInfo {
  if (!shouldResolveText(formattedValue)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makeUserResolutionQuery(formattedValue);
  return resolveFromResolutionQuery(typedValue, resolutionQuery, makeUserTypedValueFromResolvedValue);
}

export function makeProcessTaskTypedValueFromResolvedValue(
  resolvedValue: ProcessTaskResolvedValue
): ProcessTaskTypedValue {
  return {
    type: BaseTypedValueType.PROCESS_TASK,
    id: resolvedValue.id,
    humanId: resolvedValue.humanId,
  };
}

function resolveTypedValueToProcessTask(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  formattedValue: string
): ResolvedValueInfo {
  if (!shouldResolveText(formattedValue)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makeProcessTaskResolutionQuery(formattedValue);
  return resolveFromResolutionQuery(typedValue, resolutionQuery, makeProcessTaskTypedValueFromResolvedValue);
}

function resolveTypedValueToStudies(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  formattedValue: string
): ResolvedValueInfo {
  if (!shouldResolveText(formattedValue)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makeStudiesResolutionQuery(formattedValue);
  return resolveFromResolutionQuery(
    typedValue,
    resolutionQuery,
    (resolvedValue: StudiesTypedValue): StudiesTypedValue => resolvedValue
  );
}

function resolveTypedValueToProcedures(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  formattedValue: string
): ResolvedValueInfo {
  if (!shouldResolveText(formattedValue)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makeProceduresResolutionQuery(formattedValue);
  return resolveFromResolutionQuery(
    typedValue,
    resolutionQuery,
    (resolvedValue: ProcedureTypedValue): ProcedureTypedValue => resolvedValue
  );
}

function resolveTypedValueToLibraryParameterDefinition(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  formattedValue: string
): ResolvedValueInfo {
  if (!shouldResolveText(formattedValue)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makeLibraryParameterDefinitionResolutionQuery(formattedValue);
  return resolveFromResolutionQuery(
    typedValue,
    resolutionQuery,
    (resolvedValue: LibraryParameterDefinitionTypedValue): LibraryParameterDefinitionTypedValue =>
      resolvedValue
  );
}

function resolveTypedValueToProcedureConditionReplicates(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  formattedValue: string,
  fileId: string
): ResolvedValueInfo {
  if (!shouldResolveText(formattedValue)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makeProcedureConditionReplicatesResolutionQuery(formattedValue, fileId);
  return resolveFromResolutionQuery(
    typedValue,
    resolutionQuery,
    (resolvedValue: ProcedureConditionReplicatesTypedValue) => resolvedValue
  );
}

function resolveTypedValueToUnitReference(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  formattedValue: string
): ResolvedValueInfo {
  if (!shouldResolveText(formattedValue)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makeUnitResolutionQuery(formattedValue);
  return resolveFromResolutionQuery(
    typedValue,
    resolutionQuery,
    (resolvedValue: UnitReferenceTypedValue): UnitReferenceTypedValue => resolvedValue
  );
}

export function makePartiesTypedValueFromResolvedValue(resolvedValue: PartyResolvedValue): PartiesTypedValue {
  return {
    type: BaseTypedValueType.PARTIES,
    partySummaries: resolvedValue.partySummaries,
  };
}

function resolveTypedValueToParties(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  formattedValue: string
): ResolvedValueInfo {
  if (typedValue.type === BaseTypedValueType.PARTIES) {
    return {resolvedValue: typedValue, error: null};
  }
  if (!shouldResolveText(formattedValue)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makePartyResolutionQuery(formattedValue, true);
  return resolveFromResolutionQuery(typedValue, resolutionQuery, makePartiesTypedValueFromResolvedValue);
}

// TODO(BNCH-18458): make independent of the StorableStore and the live schemaField
function resolveTypedValueToBatchLink(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  schemaField: BatchLinkSchemaField
): ResolvedValueInfo {
  const text = formatTypedValue(typedValue, {});
  if (!shouldResolveText(text)) {
    return {resolvedValue: typedValue, error: null};
  }

  const resolutionQuery = makeBatchResolutionQuery({
    text,
    isMulti: schemaField.isMulti,
    batchSchemaId: schemaField.requiredLink.batchSchema && schemaField.requiredLink.batchSchema.id,
  });
  return resolveFromResolutionQuery(typedValue, resolutionQuery, (resolvedValue: BatchResolvedValue) => {
    const tagValue = getInitialTag(schemaField);
    for (const batchId of resolvedValue.batchIds) {
      // Assume batch has been loaded, because the string resolution endpoint loads it.
      const batch = StorableStore.getBatch(batchId);
      const targetBatch = {
        id: batch!.id,
        name: batch!.name,
        fileId: batch!.file.id,
        iconId: batch!.file.iconId,
      };
      tagValue.targetBatches!.push(targetBatch);
    }
    return convertTagToTypedValue(tagValue);
  });
}

function resolveTargetFileLinksArrayTypedValue(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: ArrayTypedValue<BioentityTypedValue | BioentityLinkSchemaFieldTypedValue>,
  schemaField: BioentityLinkSchemaField
): ResolvedValueInfo {
  const targetFiles = typedValue.rawValue.flatMap(
    (subTypedValue: BioentityTypedValue | BioentityLinkSchemaFieldTypedValue) => {
      if (getLinksKeyFromTypedValue(subTypedValue) === 'targetFiles') {
        return getLinksFromTypedValue(subTypedValue) ?? [];
      }
      const response = resolveTypedValueToFileLink(resolveFromResolutionQuery, subTypedValue, schemaField);
      const resolvedValue = response.resolvedValue as BioentityLinkSchemaFieldTypedValue | undefined;
      if (!response.error && resolvedValue?.targetFiles) {
        return resolvedValue.targetFiles;
      }
      return [];
    }
  );
  return {
    resolvedValue: {
      type: schemaField.fieldType,
      value: '',
      targetFiles,
    } as TypedValue,
    error: null,
  };
}

// TODO(BNCH-18458): make independent of the Redux store and the live schemaField
function resolveTypedValueToFileLink(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  schemaField: BioentityLinkSchemaField
): ResolvedValueInfo {
  const text = formatTypedValue(typedValue, {});
  if (!shouldResolveText(text)) {
    return {resolvedValue: typedValue, error: null};
  }

  const resolutionQuery = makeFileResolutionQuery(text, {
    tagSchemaId: schemaField.requiredLink.tagSchema && schemaField.requiredLink.tagSchema.id,
    folderItemType: schemaField.requiredLink.folderItemType,
    schemaInterfaceId: schemaField.requiredLink.schemaInterface?.id,
    isMulti: schemaField.isMulti,
  });
  return resolveFromResolutionQuery(typedValue, resolutionQuery, (resolvedValue: BioentityResolvedValue) => {
    const tagValue = getInitialTag(schemaField);
    for (const bioentityId of resolvedValue.bioentityIds) {
      // Assume bioentitySummaries has been loaded, because the string resolution endpoint loads them.
      const bioentitySummary = getFileSummary(bioentityId);
      const targetFile = {
        id: bioentitySummary.id,
        name: bioentitySummary.displayLabel,
        url: bioentitySummary.editURL,
      };
      tagValue.targetFiles!.push(targetFile);
    }
    return convertTagToTypedValue(tagValue);
  });
}

function isValidTargetFileLinksArray(
  typedValue: TypedValue
): typedValue is ArrayTypedValue<BioentityTypedValue | BioentityLinkSchemaFieldTypedValue> {
  return (
    typedValue.type === BaseTypedValueType.ARRAY &&
    typedValue.rawValue.length > 0 &&
    typedValue.rawValue.every(
      (subTypedValue) =>
        subTypedValue.type === BaseTypedValueType.BIOENTITY ||
        getLinksKeyFromTypedValue(subTypedValue) === 'targetFiles'
    )
  );
}

// TODO(BNCH-18458): make independent of the Redux store
export function makeBioentityTypedValueFromResolvedValue(
  resolvedValue: BioentityResolvedValue
): BioentityTypedValue {
  assert(resolvedValue.bioentityIds.length === 1, 'Single bioentityId is expected.');
  const bioentityId = resolvedValue.bioentityIds[0];
  const bioentitySummary = getFileSummary(bioentityId);
  assert(bioentitySummary != null, 'Expected the bioentitySummary to be loaded');
  return getTypedValueFromBioentitySummary(bioentitySummary);
}

// TODO (lillyhackworth) Update to work for multiple bioentities iff in inventory table
function resolveTypedValueToEntityWithTagSchemaOrInterface(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  tagSchemaOrInterface: SchemaSummary | 'ALL'
): ResolvedValueInfo {
  const text = formatTypedValue(typedValue, {});
  if (!shouldResolveText(text)) {
    return {resolvedValue: typedValue, error: null};
  }
  const resolutionQuery = makeFileResolutionQuery(text, {
    tagSchemaId:
      tagSchemaOrInterface === 'ALL' || !isTagSchema(tagSchemaOrInterface) ? null : tagSchemaOrInterface.id,
    schemaInterfaceId:
      tagSchemaOrInterface === 'ALL' || !APIUtil.isSchemaInterface(tagSchemaOrInterface.id)
        ? null
        : tagSchemaOrInterface.id,
    isMulti: false,
  });
  return resolveFromResolutionQuery(typedValue, resolutionQuery, makeBioentityTypedValueFromResolvedValue);
}

export function makeTypedValueFromCustomResolvedValue(
  customResolvedValue: StringResolvedValue
): TypedValue | null {
  // eslint-disable-next-line benchlint/no-in-operator
  assert('typedValue' in customResolvedValue, 'Expected a typed value');
  return customResolvedValue.typedValue;
}

// TODO(BNCH-18458): make independent of the StorableStore
function resolveTypedValueToParentStorable(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  isRequired: boolean
): ResolvedValueInfo {
  const text = formatTypedValue(typedValue, {});
  if (!shouldResolveText(text)) {
    const error = isRequired ? new ExcessUtil.ValueError('Location is required.') : null;
    return {resolvedValue: typedValue, error};
  }

  const resolutionQuery = makeStorableResolutionQuery({text, isMulti: false});
  return resolveFromResolutionQuery(typedValue, resolutionQuery, (resolvedValue: StorableResolvedValue) => {
    assert(resolvedValue.storableIds.length === 1, 'Single storableId is expected.');
    const storableId = resolvedValue.storableIds[0];
    // Assume storable has been loaded, because the string resolution endpoint loads it.
    const storableSummary = StorableStore.getStorableSummary(storableId);
    assert(storableSummary != null, 'Expected storable summary to be loaded');
    const storableInfo: StorableInfo = {
      id: storableSummary.id,
      name: storableSummary.name,
      barcode: storableSummary.barcode,
    };
    return {type: BaseTypedValueType.STORABLES, storableSummaries: [storableInfo]};
  });
}

// TODO(BNCH-18458): make independent of the StorableStore and the live schemaField
function resolveTypedValueToStorableLink(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  schemaField: StorableLinkSchemaField
): ResolvedValueInfo {
  // If we already have the storable information, just apply the transformation
  if (typedValue.type === BaseTypedValueType.STORABLES) {
    const {storableSummaries} = typedValue;
    // Do best effort to get the icon
    const targetStorables = storableSummaries.map(({id, name}) => ({
      id,
      name,
      iconId: schemaField.requiredLink.storableSchema?.iconId ?? '',
    }));
    return {
      resolvedValue: {
        type: FieldType.STORABLE_LINK,
        value: '',
        targetStorables,
      },
      error: null,
    };
  }

  const text = formatTypedValue(typedValue, {});
  if (!shouldResolveText(text)) {
    return {resolvedValue: typedValue, error: null};
  }

  const resolutionQuery = makeStorableResolutionQuery({
    text,
    isMulti: schemaField.isMulti,
    storableSchemaId: schemaField.requiredLink.storableSchema?.id,
  });
  return resolveFromResolutionQuery(typedValue, resolutionQuery, (resolvedValue: StorableResolvedValue) => {
    const tagValue = getInitialTag(schemaField);
    for (const storableId of resolvedValue.storableIds) {
      // Assume storable has been loaded, because the string resolution endpoint loads it.
      const storableSummary = StorableStore.getStorableSummary(storableId);
      tagValue.targetStorables!.push({
        id: storableSummary!.id,
        name: storableSummary!.name,
        iconId: storableSummary!.storableSchema.iconId,
      });
    }
    return convertTagToTypedValue(tagValue);
  });
}

// TODO(BNCH-18458): make independent of the Redux store and the live schemaField
function resolveTypedValueToEntryLink(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  schemaField: EntryLinkSchemaField
): ResolvedValueInfo {
  const text = formatTypedValue(typedValue, {});
  if (!shouldResolveText(text)) {
    return {resolvedValue: typedValue, error: null};
  }

  const resolutionQuery = makeEntryResolutionQuery(text, schemaField.isMulti);
  return resolveFromResolutionQuery(typedValue, resolutionQuery, (resolvedValue: EntryResolvedValue) => {
    const tagValue = getInitialTag(schemaField);
    for (const entryId of resolvedValue.entryIds) {
      // Assume entry has been loaded, because the string resolution endpoint loads it.
      const entrySummary = getStore().getState().models.entrySummariesById[entryId];
      const targetEntry = {
        id: entrySummary.id,
        name: entrySummary.name,
        url: entrySummary.editURL,
        humanId: entrySummary.humanId,
      };
      tagValue.targetEntries!.push(targetEntry);
    }
    return convertTagToTypedValue(tagValue);
  });
}

function resolveTypedValueToStorable(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  isRequired: boolean,
  storableType: StorableItemType
): ResolvedValueInfo {
  // if we already have the storables in tag value format, just transform
  const targetStorables = typedValue.type === FieldType.STORABLE_LINK ? typedValue.targetStorables : null;
  if (targetStorables) {
    try {
      return {
        resolvedValue: makeStorablesTypedValueFromResolvedValue({
          storableIds: targetStorables.map((item) => item.id),
        }),
        error: null,
      };
    } catch (error) {
      // if the storable is not loaded in the store, makeStorablesTypedValueFromResolvedValue will throw
      // an assertion error. Normal string resolution will load the store better.
      if (!error.message.includes(STORABLE_STORE_NOT_LOADED_ERROR_MESSAGE)) {
        throw error;
      }
    }
  }
  const text = formatTypedValue(typedValue, {});
  if (!shouldResolveText(text)) {
    const error = isRequired ? new ExcessUtil.ValueError(`A ${storableType} is required.`) : null;
    return {resolvedValue: typedValue, error};
  }
  const resolutionQuery = makeStorableResolutionQuery({text, isMulti: false, storableType});
  return resolveFromResolutionQuery(
    typedValue,
    resolutionQuery,
    (resolvedValue: StorableResolvedValue): StorablesTypedValue => {
      if (resolvedValue.storableIds.length !== 1) {
        metrics.count('TypedValueResolution.multipleStorableIdError', 1);
      }
      return makeStorablesTypedValueFromResolvedValue(resolvedValue);
    }
  );
}

function resolveTypedValueToDateType(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  fieldType: FieldType,
  languageTag: string | null,
  timezoneName: string | null
): ResolvedValueInfo {
  return resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STRUCTURED_TABLE_COERCE_TYPED_VALUE_TO_TYPE,
      fieldType,
      typedValue,
      timezoneName,
      languageTag,
      type: ResolutionQueryType.CUSTOM,
    },
    (resolvedValue: CustomResolvedValue) => resolvedValue.typedValue
  );
}

function resolveTypedValueToComplexPolymerComponent({
  resolveFromResolutionQuery,
  typedValue,
  formattedValue,
  columnDefinition,
}: {
  resolveFromResolutionQuery: ResolveQueryFn;
  typedValue: TypedValue;
  formattedValue: string;
  columnDefinition: ComplexPolymerComponentColumnDefinition;
}): ResolvedValueInfo {
  const resolutionQuery = makeFileResolutionQuery(formattedValue, {
    tagSchemaId: columnDefinition.componentSchemaField?.requiredLink?.tagSchema?.id,
    isMulti: columnDefinition.entitySelectionMode !== EntitySelectionMode.SINGLE,
  });
  return resolveFromResolutionQuery(
    typedValue,
    resolutionQuery,
    (resolvedValue: BioentityResolvedValue): ArrayTypedValue<BioentityTypedValue> => ({
      type: BaseTypedValueType.ARRAY,
      rawValue: resolvedValue.bioentityIds.flatMap((bioentityId) => {
        // Assume bioentitySummaries has been loaded, because the string resolution endpoint loads them.
        const bioentitySummary = getFileSummary(bioentityId);
        const bioentityTypedValue = getTypedValueFromBioentitySummary(bioentitySummary);
        return bioentityTypedValue ? [bioentityTypedValue] : [];
      }),
    })
  );
}

function resolveTypedValueToAntibodyDomain({
  resolveFromResolutionQuery,
  typedValue,
  formattedValue,
  columnDefinition,
}: {
  resolveFromResolutionQuery: ResolveQueryFn;
  typedValue: TypedValue;
  formattedValue: string;
  columnDefinition: AntibodyDomainColumnDefinition;
}): ResolvedValueInfo {
  // TODO(BNCH-128762) Use the specified schema from the frontend metadata to limit to that schema.
  const resolutionQuery = makeFileResolutionQuery(formattedValue, {
    folderItemType: ModelTypeConstants.PROTEIN,
    isMulti: false,
  });
  return resolveFromResolutionQuery(
    typedValue,
    resolutionQuery,
    (resolvedValue: BioentityResolvedValue): BioentityTypedValue =>
      makeBioentityTypedValueFromResolvedValue(resolvedValue)
  );
}

function makeUserResolutionQuery(text: string): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.USER,
    },
  };
}

function makeProcessTaskResolutionQuery(text: string): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.PROCESS_TASK,
    },
  };
}

function makeStudiesResolutionQuery(text: string): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.STUDY,
    },
  };
}

function makeProceduresResolutionQuery(text: string): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.PROCEDURE,
    },
  };
}

function makeLibraryParameterDefinitionResolutionQuery(text: string): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.LIBRARY_PARAMETER_DEFINITION,
    },
  };
}

function makeProcedureConditionReplicatesResolutionQuery(
  text: string,
  fileId: string
): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.PROCEDURE_CONDITION_REPLICATES,
      fileId,
    },
    isMulti: false,
  };
}

function makeUnitResolutionQuery(text: string): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.UNIT_REFERENCE,
    },
  };
}

function makePartyResolutionQuery(text: string, isMulti: boolean): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.PARTY,
    },
    isMulti,
  };
}

function makeFileResolutionQuery(
  text: string,
  {
    tagSchemaId,
    folderItemType,
    schemaInterfaceId,
    isMulti,
  }: {
    tagSchemaId?: string | null;
    folderItemType?: string | null;
    schemaInterfaceId?: string | null;
    isMulti: boolean;
  }
): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.BIOENTITY,
      ...(folderItemType && {folderItemType}),
      ...(tagSchemaId && {tagSchemaId}),
      ...(schemaInterfaceId && {schemaInterfaceId}),
    },
    isMulti,
  };
}

function makeBatchResolutionQuery(args: {
  text: string;
  batchSchemaId?: string | null;
  isMulti: boolean;
}): StringResolutionQuery {
  return {
    string: args.text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.BATCH,
      batchSchemaId: args.batchSchemaId,
    },
    isMulti: args.isMulti,
  };
}

function makeDropdownResolutionQuery(
  text: string,
  schemaFieldId: string,
  isMulti: boolean
): StringResolutionQuery {
  return {
    string: text,
    type: ResolutionQueryType.STRING,
    valueType: {
      type: StringResolutionType.DROPDOWN,
      schemaFieldId,
    },
    isMulti,
  };
}

function makeStorableResolutionQuery({
  text,
  isMulti,
  storableType,
  storableSchemaId,
}: {
  text: string;
  isMulti: boolean;
  storableType?: StorableItemType;
  storableSchemaId?: string;
}): StringResolutionQuery {
  return {
    type: ResolutionQueryType.STRING,
    string: text,
    valueType: {type: StringResolutionType.STORABLE, storableType, storableSchemaId},
    isMulti,
  };
}

function makeEntryResolutionQuery(text: string, isMulti: boolean): StringResolutionQuery {
  return {
    type: ResolutionQueryType.STRING,
    string: text,
    valueType: {type: StringResolutionType.ENTRY},
    isMulti,
  };
}

export type ResolveQueryFn = (
  typedValue: TypedValue,
  resolutionQuery: ResolutionQuery,
  makeTypedValueFromResolvedValue: (resolvedValue: StringResolvedValue) => TypedValue | null
) => ResolvedValueInfo;

export type ResolveTypedValueToTypeFn = (
  columnDefinition: ColumnDefinition,
  typedValue: TypedValue,
  options?: Partial<ResolutionContext>
) => ResolvedValueInfo;

/**
 * Perform a live resolution on a TypedValue. Return loading state if the resolution was not already loaded.
 *
 * This function is exported to be only used for two puposes:
 *  - live evaluation in client/notebook/tables/excess/Evaluator.ts which is currently being phased out.
 *  - tests
 *
 * For newer use case, you should use Resolver.resolveFromResolutionQuery. In customEval, this is surfaced
 * as the parameter `resolveFromResolutionQuery`.
 */
export function _resolveFromResolutionQueryNoWait(
  typedValue: TypedValue,
  resolutionQuery: ResolutionQuery,
  makeTypedValueFromResolvedValue: (resolvedValue: StringResolvedValue) => TypedValue | null
): ResolvedValueInfo {
  const resolutionResult = StringResolutionManager.getOrFetch(resolutionQuery);
  if (!resolutionResult) {
    return {resolvedValue: typedValue, error: new ExcessUtil.LoadingError('')};
  }
  if (resolutionResult.error && !resolutionResult.isWarning) {
    return {resolvedValue: typedValue, error: new ExcessUtil.ValueError(resolutionResult.error)};
  }
  const maybeResolvedValue: TypedValue | null = resolutionResult.resolvedValue
    ? makeTypedValueFromResolvedValue(resolutionResult.resolvedValue)
    : null;
  return {
    // For most resolution queries, the goal is to get a value from the backend that we can display.
    // But for some custom resolution queries, the goal is just to validate the input.
    // For these "validation-only" queries, the resolutionResult will be null.
    // Instead of returning the resolutionResult, we can just return the input.
    resolvedValue: maybeResolvedValue ?? typedValue,
    error: resolutionResult.error ? new ExcessUtil.Warning(resolutionResult.error) : null,
  };
}

export function resolveFromResolutionQueryPromise(
  typedValue: TypedValue,
  resolutionQuery: StringResolutionQuery,
  makeTypedValueFromResolvedValue: (resolvedValue: StringResolvedValue) => TypedValue | null,
  waitSeconds: number
): Promise<ResolvedValueInfo> {
  return new Promise((resolve) => {
    function callback(timePassed: number): void {
      const resInfo = _resolveFromResolutionQueryNoWait(
        typedValue,
        resolutionQuery,
        makeTypedValueFromResolvedValue
      );
      waitSeconds -= timePassed;
      if (!(resInfo.error?.type === ExcessUtil.LoadingError.type) || waitSeconds <= 0) {
        clearInterval(intervalId);
        // TODO: We do not differentiate between "loading" vs the terminal state of "loading failed".
        // The two should both be treated as "not yet loaded" as far as evaluation is considered,
        // but it would be nice if we were able to expose this in UI state.
        resolve(resInfo);
      }
    }

    const intervalId = setInterval(() => {
      callback(1);
    }, 1000);
    callback(0);
  });
}

// Keep in sync with MAX_NUMBER_OF_CONTAINERS in benchling/notebook/transfer_tables/process_transfers.py
export const MAX_NUMBER_OF_CONTAINERS = 384;

export function resolveNumberOfContainersTypedValue(typedValue: TypedValue): ResolvedValueInfo {
  const formattedValue = formatTypedValue(typedValue);
  if (formattedValue === '') {
    return {
      resolvedValue: typedValue,
      error: new ExcessUtil.ValueError('Number of containers is required.'),
    };
  }
  const rawValue = parseInteger(formattedValue);
  if (rawValue == null) {
    return {
      resolvedValue: typedValue,
      error: new ExcessUtil.ValueError(`${formattedValue} is not a valid integer.`),
    };
  }
  return {
    resolvedValue: {
      type: BaseTypedValueType.NUMBER,
      rawValue,
    },
    error: (() => {
      if (rawValue <= 0) {
        return new ExcessUtil.ValueError('Number of containers must be at least 1.');
      } else if (rawValue > MAX_NUMBER_OF_CONTAINERS) {
        return new ExcessUtil.ValueError(
          `Number of containers cannot be greater than ${MAX_NUMBER_OF_CONTAINERS}.`
        );
      } else {
        return null;
      }
    })(),
  };
}

export function resolveParentStorableTypedValue(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  {
    isForTransfer,
    parentStorableType,
    containerSchemaId,
  }: {
    isForTransfer: boolean;
    parentStorableType: ParentStorableType;
    containerSchemaId: string | null;
  }
): ResolvedValueInfo {
  const isRequired = isForTransfer;

  if (typedValue.type !== BaseTypedValueType.STORABLES) {
    const resolvedCell = resolveTypedValueToParentStorable(
      resolveFromResolutionQuery,
      typedValue,
      isRequired
    );
    if (resolvedCell.error || resolvedCell.resolvedValue?.type !== BaseTypedValueType.STORABLES) {
      return resolvedCell;
    }
    typedValue = resolvedCell.resolvedValue;
  }

  if (typedValue.storableSummaries.length !== 1) {
    const error = isRequired ? new ExcessUtil.ValueError('Location is required.') : null;
    return {resolvedValue: typedValue, error};
  }

  const storableInfo = typedValue.storableSummaries[0];
  const modelType = APIUtil.apiIdToType(storableInfo.id);
  const modelTypeAsGridType: GridType | undefined = Object.values<string>(GridType).includes(modelType)
    ? (modelType as unknown as GridType)
    : undefined;
  if (modelTypeAsGridType === undefined && parentStorableType === ParentStorableType.GRID) {
    const error = new ExcessUtil.ValueError('Location must be a plate or box.');
    return {resolvedValue: typedValue, error};
  }

  if (parentStorableType === ParentStorableType.LOCATION && modelType !== ModelTypeConstants.LOCATION) {
    const error = new ExcessUtil.ValueError('Location cannot be a plate, box, or container.');
    return {resolvedValue: typedValue, error};
  }

  if (!isForTransfer && !containerSchemaId) {
    // No further validation needed
    return {resolvedValue: typedValue, error: null};
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.VALIDATE_PARENT_STORABLE,
      storableId: storableInfo.id,
      isForTransfer,
      expectedGridContainerSchemaId: containerSchemaId ?? null,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {resolvedValue, error};
}

class StorableStoreNotLoadedError extends Error {}

const STORABLE_STORE_NOT_LOADED_ERROR_MESSAGE = 'Expected storable summary to be loaded';
// TODO(BNCH-18458): make independent of the StorableStore
function makeStorablesTypedValueFromResolvedValue(resolvedValue: StorableResolvedValue): StorablesTypedValue {
  const storableId = resolvedValue.storableIds[0];
  // Assume storable has been loaded, because the string resolution endpoint loads it.
  const storableSummary = StorableStore.getStorableSummary(storableId);
  if (storableSummary == null) {
    throw new StorableStoreNotLoadedError(STORABLE_STORE_NOT_LOADED_ERROR_MESSAGE);
  }
  const storableInfo: StorableInfo = {
    id: storableSummary.id,
    name: storableSummary.name,
    barcode: storableSummary.barcode,
  };
  return {type: BaseTypedValueType.STORABLES, storableSummaries: [storableInfo]};
}

export function resolveStorableTypedValue(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition
): ResolvedValueInfo {
  let storableType: StorableItemType | undefined;
  if (columnDefinition.type === TableColumnType.PLATE) {
    storableType = ModelTypeConstants.PLATE;
  } else if (columnDefinition.type === TableColumnType.BOX) {
    storableType = ModelTypeConstants.BOX;
  } else if (columnDefinition.type === TableColumnType.CONTAINERS) {
    storableType = ModelTypeConstants.CONTAINER;
  } else {
    metrics.count('TypedValueResolution.unsupportedColumnTypeError', 1, {columnType: columnDefinition.type});
    return {resolvedValue: typedValue, error: null};
  }
  const {isRequired} = columnDefinition;

  if (
    typedValue.type !== BaseTypedValueType.STORABLES &&
    typedValue.type !== BaseTypedValueType.STORABLE_PLACEHOLDERS
  ) {
    const resolvedCell = resolveTypedValueToStorable(
      resolveFromResolutionQuery,
      typedValue,
      isRequired,
      storableType
    );
    if (resolvedCell.error || resolvedCell.resolvedValue?.type !== BaseTypedValueType.STORABLES) {
      return resolvedCell;
    }
    typedValue = resolvedCell.resolvedValue;
  }

  if (typedValue.type !== BaseTypedValueType.STORABLES) {
    return {resolvedValue: typedValue, error: null};
  }

  if (typedValue.storableSummaries.length > 1) {
    const error = new ExcessUtil.ValueError(`Cannot have more than one ${storableType}.`);
    return {resolvedValue: typedValue, error};
  }

  if (typedValue.storableSummaries.length !== 1) {
    const error = isRequired ? new ExcessUtil.ValueError(`A ${storableType} is required.`) : null;
    return {resolvedValue: typedValue, error};
  }

  const storableInfo = typedValue.storableSummaries[0];
  const modelType = APIUtil.apiIdToType(storableInfo.id);

  if ((modelType as string) !== (storableType as string)) {
    const error = new ExcessUtil.ValueError(`Must be a ${storableType}, found ${modelType} instead`);
    return {resolvedValue: typedValue, error};
  }

  return {resolvedValue: typedValue, error: null};
}

/**
 * Resolve a typedValue to any matching storable typed value.
 * Used to see if inputted barcode will resolve to any storable.
 */
export function resolveAnyStorableTypedValue(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue
): ResolvedValueInfo {
  const text = formatTypedValue(typedValue, {});
  const resolutionQuery = makeStorableResolutionQuery({text, isMulti: false});
  return resolveFromResolutionQuery(typedValue, resolutionQuery, makeStorablesTypedValueFromResolvedValue);
}

/**
 * Perform string resolution.
 *
 * We are using live values of schemaField as opposed to schema field values snapshotted in columnDefinition
 * because columnDefinition doesn't have all the data we need for string resolution
 */
// TODO(BNCH-18458): make independent of the live schemaField
function resolveTypedValueForSchemaFieldWithoutLegalText(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  schemaField: SchemaField,
  languageTag: string | null,
  timezoneName: string | null
): ResolvedValueInfo {
  switch (schemaField.fieldType) {
    case FIELD_TYPES.SELECTOR.type:
      return resolveTypedValueToSelectorOptions(resolveFromResolutionQuery, typedValue, schemaField);

    case FIELD_TYPES.BIOENTITY_LINK.type:
    case FIELD_TYPES.TRANSLATION_LINK.type:
    case FIELD_TYPES.PART_LINK.type:
    case FIELD_TYPES.AA_PART_LINK.type:
    case FIELD_TYPES.TRANSCRIPTION_LINK.type:
      if (isValidTargetFileLinksArray(typedValue)) {
        return resolveTargetFileLinksArrayTypedValue(
          resolveFromResolutionQuery,
          typedValue,
          schemaField as BioentityLinkSchemaField
        );
      } else {
        // The casting here and below can be removed once the SchemaField type is defined as a tagged union
        return resolveTypedValueToFileLink(
          resolveFromResolutionQuery,
          typedValue,
          schemaField as BioentityLinkSchemaField
        );
      }

    case FIELD_TYPES.BATCH_LINK.type:
      return resolveTypedValueToBatchLink(
        resolveFromResolutionQuery,
        typedValue,
        schemaField as BatchLinkSchemaField
      );

    case FIELD_TYPES.STORABLE_LINK.type:
      return resolveTypedValueToStorableLink(
        resolveFromResolutionQuery,
        typedValue,
        schemaField as StorableLinkSchemaField
      );

    case FIELD_TYPES.ENTRY_LINK.type:
      return resolveTypedValueToEntryLink(
        resolveFromResolutionQuery,
        typedValue,
        schemaField as EntryLinkSchemaField
      );

    case FIELD_TYPES.DATE.type:
    case FIELD_TYPES.DATETIME.type:
      return resolveTypedValueToDateType(
        resolveFromResolutionQuery,
        typedValue,
        schemaField.fieldType,
        languageTag,
        timezoneName
      );

    default:
      if (isTypedValueEmpty(typedValue)) {
        return {resolvedValue: typedValue, error: null};
      } else {
        const textValue = formatTypedValue(typedValue);
        const fieldTypeName = getFieldTypeName(schemaField.fieldType);
        return {
          resolvedValue: typedValue,
          error: new ExcessUtil.ValueError(`${textValue} is not a valid ${fieldTypeName}`),
        };
      }
  }
}

function resolveTypedValueToDate(typedValue: TypedValue, formattedValue: string): ResolvedValueInfo {
  const dateValue = tryParseDatetimeAsDate(formattedValue);
  if (dateValue == null) {
    return {
      resolvedValue: typedValue,
      error:
        formattedValue != null && formattedValue !== ''
          ? new ExcessUtil.ValueError(`${formattedValue} is not a valid date.`)
          : null,
    };
  }
  return {
    resolvedValue: {
      type: BaseTypedValueType.DATE,
      value: dateValue,
    },
    error: null,
  };
}

// TODO(BNCH-18458): make independent of columnDefinition.schemaField, which is a live value that depends on the user's permissions
function resolveTypedValueForSchemaField(
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  columnDefinition: SchemaFieldColumnDefinition,
  languageTag: string | null,
  tableType: string | null | undefined
): ResolvedValueInfo {
  if (
    columnDefinition.skipSchemaValidation ||
    (tableType === ModelTypeConstants.DATA_TABLE &&
      [FIELD_TYPES.TEXT.type, FIELD_TYPES.LONG_TEXT.type].includes(columnDefinition.tagSchemaFieldType))
  ) {
    // Special-case data table text fields for backward compatibility.
    // Numeric values should be handled like in regular ELN tables, and not like strict text values.
    return {resolvedValue: typedValue, error: null};
  }

  if (isValueCompatibleWithColumn(columnDefinition, typedValue)) {
    // No need to resolve if already the proper type
    return {resolvedValue: typedValue, error: null};
  }

  let originalResult;
  if (
    canParseTypedValueFromString(columnDefinition.tagSchemaFieldType) &&
    !shouldTypedValueBeResolvedAsync(columnDefinition.schemaField.fieldType, false)
  ) {
    // No need for string resolution, we can figure out the typed value based on text without server query
    const {tagSchemaFieldType, schemaField} = columnDefinition;
    originalResult = coerceTypedValueToType(typedValue, tagSchemaFieldType, languageTag, {
      numericMin: schemaField.numericMin,
      numericMax: schemaField.numericMax,
      schemaDecimalPrecision: columnDefinition.tagSchemaFieldDecimalPrecision,
    });
  } else {
    originalResult = resolveTypedValueForSchemaFieldWithoutLegalText(
      resolveFromResolutionQuery,
      typedValue,
      columnDefinition.schemaField,
      languageTag,
      null
    );
  }

  if (originalResult.error && columnDefinition.schemaField.legalTextSelectorId) {
    // Try resolving as legal text, returning that error if it occurs. The main reason to do this is because
    // the user should generally understand that the float field supports numbers (even if we don't say it),
    // whereas without this message, the user probably wouldn't know exactly which options are allowed.
    return resolveTypedValueToSelectorOptions(
      resolveFromResolutionQuery,
      typedValue,
      columnDefinition.schemaField
    );
  }

  return originalResult;
}

function isValueCompatibleWithColumn(
  columnDefinition: SchemaFieldColumnDefinition,
  typedValue: TypedValue
): boolean {
  if (typedValue.type === BaseTypedValueType.COMPUTING && columnDefinition.tagSchemaFieldIsComputed) {
    return true;
  }
  if (typedValue.type !== columnDefinition.tagSchemaFieldType) {
    // TODO(BNCH-101106): Remove the following condition once we have a better way to handle array-typed columns
    // Allow ArrayTypedValues for multi-primitive schema fields. This should only be used for non-persisted parameter tables.
    if (
      typedValue.type === BaseTypedValueType.ARRAY &&
      typedValue.rawValue[0]?.type === columnDefinition.tagSchemaFieldType &&
      columnDefinition.schemaField.isMulti &&
      isPrimitiveMultiFieldType(columnDefinition.tagSchemaFieldType)
    ) {
      return true;
    }
    return false;
  }
  if (columnDefinition.selector != null && typedValue.type === FieldType.SELECTOR) {
    // The column has constraint on possible options. If one of the text values doesn't match the
    // available options, we should try resolving it.
    const schemaFieldSelectorOptionIds = columnDefinition.selector.schemaFieldSelectorOptions.map(
      (option) => option.id
    );
    const areAllOptionsCompatible = typedValue.schemaFieldSelectorOptionIds?.every((optionId) =>
      schemaFieldSelectorOptionIds.includes(optionId)
    );
    return Boolean(areAllOptionsCompatible);
  }
  if (
    columnDefinition.tagSchemaFieldType === FieldType.DATETIME &&
    typedValue.type === FieldType.DATETIME &&
    !typedValue.datetimeValue
  ) {
    return false;
  }
  if (
    columnDefinition.tagSchemaFieldType === FieldType.DATE &&
    typedValue.type === FieldType.DATE &&
    !typedValue.dateValue
  ) {
    return false;
  }
  // Float tag values may differ in their display precision, but we can infer compatibility in some cases
  if (typedValue.type === FieldType.FLOAT) {
    if (
      !isTypedValueEmpty(typedValue) &&
      (columnDefinition.tagSchemaFieldDecimalPrecision != null || typedValue.value !== typedValue.rawValue)
    ) {
      return false;
    }
  }
  return true;
}

export function resolveNaturalBasesValue(
  formattedValue: string,
  nucleotideType: NucleotideType,
  maxLength: number,
  forOligo: boolean = false
): ResolvedValueInfo {
  let basesAreValid;
  if (forOligo && isFullUTFlexibilityEnabled()) {
    basesAreValid = biojs.util.isValidMixedBaseSequence(formattedValue);
  } else {
    basesAreValid = biojs.util.isValidSequence(formattedValue, nucleotideType);
  }
  const basesError = !basesAreValid
    ? new ExcessUtil.ValueError(`${ellipsize(formattedValue, 20)} is not a valid ${nucleotideType} sequence.`)
    : null;
  const lengthError =
    BenchlingConfig.MOLBIO_ENFORCE_SEQUENCE_LENGTH_LIMITS && formattedValue.length > maxLength
      ? new ExcessUtil.ValueError(`Exceeds maximum length of ${maxLength} bases.`)
      : null;
  return {
    resolvedValue: {
      type: BaseTypedValueType.STRING,
      rawValue: formattedValue,
    },
    error: lengthError ?? basesError,
  };
}

function resolveSMILESValue(
  formattedValue: string,
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue
): ResolvedValueInfo {
  const {error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_SMILES,
      smilesString: formattedValue,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {
    resolvedValue: {
      type: BaseTypedValueType.STRING,
      rawValue: formattedValue,
    },
    error,
  };
}

export function resolveHELMOrIDTValue(
  formattedValue: string,
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  nucleotideType: NucleotideType,
  notation: Notation
): ResolvedValueInfo {
  const query: ResolutionQuery = {
    ...(notation === Notation.HELM
      ? {
          customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_HELM,
          helmString: formattedValue,
        }
      : {
          customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_IDT,
          idtString: formattedValue,
        }),
    nucleotideType,
    type: ResolutionQueryType.CUSTOM,
  };

  const {error} = resolveFromResolutionQuery(typedValue, query, makeTypedValueFromCustomResolvedValue);
  return {
    resolvedValue: {
      type: BaseTypedValueType.STRING,
      rawValue: formattedValue,
    },
    error,
  };
}

/*
 * Given the current column definition and a typed value for the evaluated version of that cell, do
 * any necessary further resolution to fit this column definition.
 *
 * Return an object of the form {resolvedValue, error}. resolvedValue is required, and is
 * best-effort resolution (keeping the original typedValue if no resolution can be done). error is
 * optional, and is a user-facing error message.
 *
 * To maintain backward compatibility, data table text fields are *not* resolved.
 *
 * @returns {resolvedValue: TypedValue, error: null} for typical resolution.
 * @returns {resolvedValue: TypedValue, error: ValueError} for resolution with a warning.
 * @throws ValueError - cell will resolve to a "#VALUE!" error value.
 */
export function resolveTypedValue(
  resolveFromResolutionQuery: ResolveQueryFn,
  columnDefinition: ColumnDefinition,
  typedValue: TypedValue,
  languageTag: string | null,
  {tableType, hasFormula, fileId}: Partial<ResolutionContext> = {}
): ResolvedValueInfo {
  if (typedValue.type === BaseTypedValueType.ARRAY && typedValue.rawValue.length === 1) {
    typedValue = typedValue.rawValue[0];
  }

  if (columnDefinition.type === TableColumnType.REQUEST_TASK_SELECTION) {
    return {resolvedValue: typedValue, error: null};
  }

  if (columnDefinition.isRequired && isTypedValueEmpty(typedValue)) {
    // for schema fields, resolve the value to the proper type instead of returning the empty string typed value
    const resolvedValue =
      columnDefinition.type === TableColumnType.TAG_VALUE
        ? resolveTypedValueForSchemaField(
            resolveFromResolutionQuery,
            typedValue,
            columnDefinition,
            languageTag,
            tableType
          ).resolvedValue
        : typedValue;

    return {
      resolvedValue,
      error: new ExcessUtil.ValueError(ExcessUtil.REQUIRED_FIELD_ERROR_MESSAGE),
    };
  }

  // Computed entity columns are nullable and managed by the backend. They are calculated after the custom
  // entity is created or updated. Consequently, their errors must be non-blocking so that the user can update
  // the other fields on the custom entity that might be leading to the error in the computed field.
  if (typedValue.type === BaseTypedValueType.COMPUTING && typedValue.computedState.status === Status.FAILED) {
    return {
      resolvedValue: toStringTypedValue(''),
      error: new ExcessUtil.ComputedEntityFieldError(
        typedValue.computedState.errorMessage ? typedValue.computedState.errorMessage : 'Computing error'
      ),
    };
  }

  let formattedValue = formatTypedValue(typedValue);

  // Ignore carriage returns
  formattedValue = removeCarriageReturn(formattedValue);

  switch (columnDefinition.type) {
    case TableColumnType.ALIAS:
      if (typedValue.type === BaseTypedValueType.ARRAY) {
        return {
          resolvedValue: typedValue,
          error: typedValue.rawValue.every((aliasValue) => aliasValue.type === BaseTypedValueType.STRING)
            ? null
            : new ExcessUtil.ValueError('Alias must be an array of strings.'),
        };
      }
      return {
        resolvedValue: {
          type: BaseTypedValueType.ARRAY,
          rawValue: [{type: BaseTypedValueType.STRING, rawValue: formattedValue}],
        },
        error: null,
      };

    case TableColumnType.PROCESS_TASK_ASSIGNEE: {
      if (typedValue.type === BaseTypedValueType.USER) {
        return {
          resolvedValue: typedValue,
          error: null,
        };
      }
      return resolveTypedValueToUser(resolveFromResolutionQuery, typedValue, formattedValue);
    }

    case TableColumnType.TAG_VALUE: {
      return resolveTypedValueForSchemaField(
        resolveFromResolutionQuery,
        typedValue,
        columnDefinition,
        languageTag,
        tableType
      );
    }

    case TableColumnType.EXISTING_BIOENTITY: {
      if (formattedValue === '' && hasFormula) {
        return {
          resolvedValue: typedValue,
          error: new ExcessUtil.ValueError('Formula result is not a valid bioentity.'),
        };
      }
      if (getBioentityInfosFromTypedValue(typedValue) || formattedValue === '') {
        // Return array typed values from inventory table and non-array typed values always
        if (
          typedValue.type !== BaseTypedValueType.ARRAY ||
          (tableType != null && tableTypeToKind(tableType).kind === StructuredTableKind.INVENTORY)
        ) {
          return {resolvedValue: typedValue, error: null};
        }
        // Outside of inventory table, just return array of BioentityTypedValues as a formatted string
        // TODO(BNCH-41594) Properly resolve array typed values
        const errorMessage =
          typedValue.rawValue.length > 1
            ? 'Column does not support multiple entities'
            : 'Please re-enter value manually';
        return {
          resolvedValue: {type: BaseTypedValueType.STRING, rawValue: formattedValue},
          error: new ExcessUtil.ValueError(errorMessage),
        };
      }
      return resolveTypedValueToEntityWithTagSchemaOrInterface(
        resolveFromResolutionQuery,
        typedValue,
        columnDefinition.tagSchemaOrInterface
      );
    }

    case TableColumnType.STAGE_RUN: {
      return resolveStageRunValue(typedValue, columnDefinition.stageRuns);
    }

    case TableColumnType.BIOENTITY_NAME: {
      // Don't show an error if the column is read-only
      const error =
        formattedValue === '' && !columnDefinition.locked
          ? new ExcessUtil.ValueError('Entity name is required.')
          : null;
      return {
        resolvedValue: {
          type: BaseTypedValueType.STRING,
          rawValue: formattedValue,
        },
        error,
      };
    }

    case TableColumnType.DNA_BASES: {
      return resolveNaturalBasesValue(formattedValue, NucleotideType.DNA, OLIGO_MAX_LENGTH);
    }

    case TableColumnType.RNA_BASES: {
      return resolveNaturalBasesValue(formattedValue, NucleotideType.RNA, OLIGO_MAX_LENGTH);
    }

    case TableColumnType.DNA_HELM:
    case TableColumnType.RNA_HELM: {
      return resolveHELMOrIDTValue(
        formattedValue,
        resolveFromResolutionQuery,
        typedValue,
        columnDefinition.type === TableColumnType.DNA_HELM ? NucleotideType.DNA : NucleotideType.RNA,
        Notation.HELM
      );
    }

    case TableColumnType.DNA_IDT:
    case TableColumnType.RNA_IDT: {
      return resolveHELMOrIDTValue(
        formattedValue,
        resolveFromResolutionQuery,
        typedValue,
        columnDefinition.type === TableColumnType.DNA_IDT ? NucleotideType.DNA : NucleotideType.RNA,
        Notation.IDT
      );
    }

    case TableColumnType.SMILES: {
      return resolveSMILESValue(formattedValue, resolveFromResolutionQuery, typedValue);
    }

    case TableColumnType.NUMBER_OF_CONTAINERS: {
      return resolveNumberOfContainersTypedValue(typedValue);
    }
    case TableColumnType.CONCENTRATION: {
      if (formattedValue === '') {
        return {
          resolvedValue: typedValue,
          error: null,
        };
      }
      const rawValue = parseNumber(formattedValue);
      if (rawValue == null || rawValue < 0) {
        return {
          resolvedValue: typedValue,
          error: new ExcessUtil.ValueError(`${formattedValue} is not a valid positive number.`),
        };
      }
      return {
        resolvedValue: {
          type: BaseTypedValueType.NUMBER,
          rawValue,
        },
        error: null,
      };
    }

    case TableColumnType.PARENT_STORABLE: {
      return resolveParentStorableTypedValue(resolveFromResolutionQuery, typedValue, {
        isForTransfer: columnDefinition.isForTransfer,
        parentStorableType: columnDefinition.parentStorableType,
        containerSchemaId:
          columnDefinition.storableSchema && APIUtil.isContainerSchema(columnDefinition.storableSchema.id)
            ? columnDefinition.storableSchema.id
            : null,
      });
    }

    case TableColumnType.BOX:
    case TableColumnType.PLATE: {
      // Do not resolve if not in transfer, inventory, or lookup table
      if (
        tableType == null ||
        (tableTypeToKind(tableType).kind !== StructuredTableKind.TRANSFER &&
          tableTypeToKind(tableType).kind !== StructuredTableKind.INVENTORY &&
          tableTypeToKind(tableType).kind !== StructuredTableKind.LOOKUP)
      ) {
        return {resolvedValue: typedValue, error: null};
      }
      return resolveStorableTypedValue(resolveFromResolutionQuery, typedValue, columnDefinition);
    }

    case TableColumnType.PARTIES: {
      return resolveTypedValueToParties(resolveFromResolutionQuery, typedValue, formattedValue);
    }

    case TableColumnType.SAMPLE_RESTRICTION_STATUS: {
      const status = getRestrictionStatusFromCaseInsensitiveLabel(formattedValue);
      if (status == null) {
        return {
          resolvedValue: typedValue,
          error:
            formattedValue != null && formattedValue !== ''
              ? new ExcessUtil.ValueError(`${formattedValue} is not a valid restriction status.`)
              : null,
        };
      }
      return {
        resolvedValue: {
          type: BaseTypedValueType.SAMPLE_RESTRICTION_STATUS,
          status,
        },
        error: null,
      };
    }

    case TableColumnType.INGREDIENT_CATALOG_ID:
    case TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT:
    case TableColumnType.INGREDIENT_NOTES:
    case TableColumnType.COMPLEX_POLYMER_CONNECTIONS:
    case TableColumnType.STRING: {
      return {
        resolvedValue: {
          type: BaseTypedValueType.STRING,
          rawValue: formattedValue,
        },
        error: null,
      };
    }

    case TableColumnType.BOOLEAN: {
      return resolveTypedValueToBoolean(typedValue);
    }

    case TableColumnType.PROCESS_OUTPUT: {
      return {resolvedValue: typedValue, error: null};
    }

    case TableColumnType.PROCESS_TASK:
      if (typedValue.type === BaseTypedValueType.PROCESS_TASK) {
        return {resolvedValue: typedValue, error: null};
      }
      return resolveTypedValueToProcessTask(resolveFromResolutionQuery, typedValue, formattedValue);

    case TableColumnType.STUDIES:
      if (typedValue.type === BaseTypedValueType.STUDIES) {
        return {resolvedValue: typedValue, error: null};
      }
      return resolveTypedValueToStudies(resolveFromResolutionQuery, typedValue, formattedValue);

    case TableColumnType.PROCEDURE:
      if (typedValue.type === BaseTypedValueType.PROCEDURE) {
        return {resolvedValue: typedValue, error: null};
      }
      return resolveTypedValueToProcedures(resolveFromResolutionQuery, typedValue, formattedValue);

    case TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR:
      if (typedValue.type === BaseTypedValueType.LIBRARY_PARAMETER_DEFINITION) {
        return {resolvedValue: typedValue, error: null};
      }
      return resolveTypedValueToLibraryParameterDefinition(
        resolveFromResolutionQuery,
        typedValue,
        formattedValue
      );

    case TableColumnType.PROCEDURE_CONDITION_REPLICATES: {
      if (!fileId || !APIUtil.isDocument(fileId)) {
        return {
          resolvedValue: typedValue,
          error: new ExcessUtil.ValueError('Condition-Replicates can only be resolved in an Experiment.'),
        };
      }
      // If we are given a ProcedureConditionReplicate TypedValue, we can try to see if it's an acceptable
      // PMEI for the given document by checking our FE stores. This is simultaneously validating that the
      // given PMEI matches the document, but will fallback to trying string resolution.
      // TODO(BNCH-121087): This is a temporary fix to prevent the need for showing loading indicators to the
      // user. We instead want to make the loading status less abrasive rather than use this additional
      // store workaround.
      if (
        typedValue.type === BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES &&
        typedValue.conditionReplicates.every((cr) =>
          getPMEIsForDocument(fileId)?.has(cr.procedureMethodExecutionInstanceId)
        )
      ) {
        return {resolvedValue: typedValue, error: null};
      }
      // Even if we were provided a ProcedureConditionReplicate TypedValue, we want to re-resolve it if it's
      // not known to have a valid PMEI for the given context.
      return resolveTypedValueToProcedureConditionReplicates(
        resolveFromResolutionQuery,
        typedValue,
        formattedValue,
        fileId
      );
    }

    case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN: {
      return {
        resolvedValue: typedValue,
        error: null,
      };
    }

    case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE: {
      return resolveTypedValueToDate(typedValue, formattedValue);
    }

    case TableColumnType.UNIT_SELECTOR: {
      assert(typedValue.type === BaseTypedValueType.STRING, 'Unit selector must have a string typedValue', {
        typedValue,
      });
      return {
        resolvedValue: typedValue,
        error:
          isTypedValueEmpty(typedValue) || columnDefinition.acceptedUnits.includes(typedValue.rawValue)
            ? null
            : new ExcessUtil.ValueError(`${typedValue.rawValue} is not a valid unit.`),
      };
    }

    case TableColumnType.UNIT_REFERENCE:
      if (
        typedValue.type === BaseTypedValueType.UNIT_REFERENCE ||
        isEqual(typedValue, columnDefinition.ignoreErrorWhenTypedValue)
      ) {
        return {resolvedValue: typedValue, error: null};
      }
      return resolveTypedValueToUnitReference(resolveFromResolutionQuery, typedValue, formattedValue);

    case TableColumnType.OPTION_SELECTOR: {
      let hasError: boolean;
      let typedValueErrorDisplay: string;
      if (typedValue.type === BaseTypedValueType.STRING) {
        assert(
          columnDefinition.options.every((option) => typeof option === 'string'),
          'Option selector with string typedValue must have string options',
          {typedValue}
        );
        hasError = !columnDefinition.options.includes(typedValue.rawValue);
        typedValueErrorDisplay = typedValue.rawValue;
      } else if (typedValue.type === BaseTypedValueType.CLIENT_IDENTIFIABLE) {
        assert(
          columnDefinition.options.every((option) => typeof option === 'object'),
          'Option selector with client-identifiable typedValue must have OptionCellDropdownOption options',
          {typedValue}
        );
        hasError = !columnDefinition.options.some(
          ({onUpdateKey}: OptionCellDropdownOption) => onUpdateKey === typedValue.id
        );
        typedValueErrorDisplay = typedValue.name;
      } else {
        assert(false, 'Option selector must have a string or client-identifiable typedValue', {typedValue});
      }

      return {
        resolvedValue: typedValue,
        error: hasError
          ? new ExcessUtil.ValueError(`"${typedValueErrorDisplay}" is not a valid option.`)
          : null,
      };
    }

    case TableColumnType.OPTION_SEARCH_SELECTOR: {
      let hasError: boolean;
      let typedValueErrorDisplay: string;
      if (typedValue.type === BaseTypedValueType.ARRAY) {
        assert(
          columnDefinition.isMulti ?? false,
          'Option search selector with array typedValue must have isMulti set to true'
        );
        assert(
          typedValue.rawValue.every((value) => value.type === BaseTypedValueType.STRING),
          'Option search selector with array typedValue must have string elements',
          {typedValue}
        );

        hasError = typedValue.rawValue.some((value) => !columnDefinition.options.includes(value.rawValue));
        typedValueErrorDisplay = typedValue.rawValue.map((value) => value.rawValue).join(', ');
      } else if (typedValue.type === BaseTypedValueType.STRING) {
        assert(
          columnDefinition.options.every((option) => typeof option === 'string'),
          'Option search selector with string typedValue must have string options',
          {typedValue}
        );
        hasError = !columnDefinition.options.includes(typedValue.rawValue);
        typedValueErrorDisplay = typedValue.rawValue;
      } else if (typedValue.type === BaseTypedValueType.CLIENT_IDENTIFIABLE) {
        assert(
          columnDefinition.options.every((option) => typeof option === 'object'),
          'Option search selector with client-identifiable typedValue must have OptionData options',
          {typedValue}
        );
        hasError = !columnDefinition.options.some(
          (option) => typeof option === 'object' && option.value === typedValue.id
        );
        typedValueErrorDisplay = typedValue.name;
      } else {
        assert(false, 'Option search selector must have a string or client-identifiable typedValue', {
          typedValue,
        });
      }

      return {
        resolvedValue: typedValue,
        error: hasError
          ? new ExcessUtil.ValueError(`"${typedValueErrorDisplay}" is not a valid option.`)
          : null,
      };
    }

    case TableColumnType.PLATE_LAYOUT_SELECTOR: {
      assert(
        typedValue.type === BaseTypedValueType.PLATE_LAYOUT,
        'Plate layout selector must have a plate layout typedValue',
        {
          typedValue,
        }
      );
      const canUsePlateLayout = canApplyPlateLayout(
        typedValue.source,
        typedValue.destination,
        columnDefinition.runSchema
      );
      return {
        resolvedValue: typedValue,
        error:
          typedValue.plateLayout == null || canUsePlateLayout
            ? null
            : new ExcessUtil.ValueError('Cannot use plate layout with provided source and destination'),
      };
    }
    case TableColumnType.LOOKUP_CONFIG: {
      assert(
        typedValue.type === BaseTypedValueType.LOOKUP_CONFIG_INFO,
        'Lookup config cell must have a lookup config info typedValue',
        {typedValue}
      );
      return {
        resolvedValue: typedValue,
        error: null, // TODO(BNCH-22016): Do we want any validation here?
      };
    }

    case TableColumnType.MEASUREMENT_AMOUNT:
      // This column type accepts positive numeric values, or the value "QS", and stores them as strings.
      // Negative numeric values are allowed iff columnDefinition.allowNegative is true
      // 0 is allowed iff columnDefinition.allowZero is true
      return {
        resolvedValue: {
          type: BaseTypedValueType.STRING,
          rawValue: formattedValue,
        },

        // If a value is required but not provided, the error is reported separately at the top of this function
        error:
          formattedValue === '' ||
          (columnDefinition.allowQS && isAmountQs(formattedValue)) ||
          isPositiveNumber(formattedValue) ||
          (columnDefinition.allowNegative && isNegativeNumber(formattedValue)) ||
          (columnDefinition.allowZero && isZero(formattedValue))
            ? null
            : new ExcessUtil.ValueError(`${formattedValue} is not a valid measurement amount.`),
      };

    case TableColumnType.CONTAINERS: {
      const {containersColumnMode} = columnDefinition;
      if (
        (tableType == null || tableTypeToKind(tableType).kind !== StructuredTableKind.LOOKUP) &&
        (containersColumnMode === ContainersColumnMode.LEGACY ||
          containersColumnMode === ContainersColumnMode.NEW_DESTINATION_FOR_TRANSFER_TABLE)
      ) {
        return {resolvedValue: typedValue, error: null};
      }

      return resolveStorableTypedValue(resolveFromResolutionQuery, typedValue, columnDefinition);
    }

    case TableColumnType.CUSTOM:
      {
        const dataType = columnDefinition.customColumnConfig.dataType;
        switch (dataType) {
          case CustomColumnDataType.TEXT:
            return {
              resolvedValue: {
                type: BaseTypedValueType.STRING,
                rawValue: formattedValue,
              },
              error: null,
            };
          case CustomColumnDataType.INTEGER: {
            const rawIntValue = parseInteger(formattedValue);
            if (rawIntValue == null) {
              return {
                resolvedValue: typedValue,
                error:
                  formattedValue != null && formattedValue !== ''
                    ? new ExcessUtil.ValueError(`${formattedValue} is not a valid integer.`)
                    : null,
              };
            }
            return {
              resolvedValue: {
                type: BaseTypedValueType.NUMBER,
                rawValue: rawIntValue,
              },
              error: null,
            };
          }
          case CustomColumnDataType.FLOAT: {
            const floatValue = parseTextAsFloat(formattedValue);
            if (floatValue == null) {
              return {
                resolvedValue: typedValue,
                error:
                  formattedValue != null && formattedValue !== ''
                    ? new ExcessUtil.ValueError(`${formattedValue} is not a valid number.`)
                    : null,
              };
            }

            if (typedValue.type === FieldType.FLOAT) {
              return {
                resolvedValue: typedValue,
                error: null,
              };
            }
            return {
              resolvedValue: {
                type: FieldType.FLOAT,
                value: formattedValue,
                rawValue: formattedValue,
                floatValue,
              },
              error: null,
            };
          }
          case CustomColumnDataType.BOOLEAN: {
            return resolveTypedValueToBoolean(typedValue);
          }
          case CustomColumnDataType.DATE: {
            return resolveTypedValueToDate(typedValue, formattedValue);
          }
          case CustomColumnDataType.DATETIME: {
            const datetimeValue = tryParse(formattedValue);
            if (datetimeValue == null) {
              return {
                resolvedValue: typedValue,
                error:
                  formattedValue != null && formattedValue !== ''
                    ? new ExcessUtil.ValueError(`${formattedValue} is not a valid datetime.`)
                    : null,
              };
            }

            // If already using the schema field datetime type, preserve it, like FLOAT
            if (typedValue.type === FieldType.DATETIME) {
              return {
                resolvedValue: {
                  type: FieldType.DATETIME,
                  value: datetimeValue,
                  datetimeValue,
                },
                error: null,
              };
            }

            return {
              resolvedValue: {
                type: BaseTypedValueType.DATETIME,
                value: datetimeValue,
              },
              error: null,
            };
          }
          default:
            assertNever(dataType);
            assert(false, 'You need to add a new resolver if you added a new custom column data type.');
        }
      }
      break;

    case TableColumnType.COMPLEX_POLYMER_COMPONENT: {
      if (formattedValue === '') {
        return {
          resolvedValue: typedValue,
          error: null,
        };
      }
      if (typedValue.type === BaseTypedValueType.BIOENTITY) {
        return {
          resolvedValue: {
            type: BaseTypedValueType.ARRAY,
            rawValue: [typedValue],
          },
          error: null,
        };
      } else if (
        typedValue.type === BaseTypedValueType.ARRAY &&
        typedValue.rawValue.every((v) => v.type === BaseTypedValueType.BIOENTITY)
      ) {
        return {
          resolvedValue: typedValue,
          error: null,
        };
      }
      return resolveTypedValueToComplexPolymerComponent({
        resolveFromResolutionQuery,
        typedValue,
        formattedValue,
        columnDefinition,
      });
    }
    case TableColumnType.OLIGO_DUPLEX_OFFSET: {
      if (formattedValue === '') {
        return {
          resolvedValue: typedValue,
          error: null,
        };
      }
      const rawValue = parseInteger(formattedValue);
      if (rawValue == null) {
        return {
          resolvedValue: typedValue,
          error:
            formattedValue != null && formattedValue !== ''
              ? new ExcessUtil.ValueError(`${formattedValue} is not a valid offset.`)
              : null,
        };
      }
      return {
        resolvedValue: {
          type: BaseTypedValueType.NUMBER,
          rawValue,
        },
        error: null,
      };
    }
    case TableColumnType.SEQUENCE_TOPOLOGY: {
      const topology = parseTopologyFromString(formattedValue);

      if (topology == null) {
        return {
          resolvedValue: typedValue,
          error: new ExcessUtil.ValueError(`${formattedValue} is not a valid topology.`),
        };
      }

      return {
        resolvedValue: {
          type: BaseTypedValueType.STRING,
          rawValue: topology,
        },
        error: null,
      };
    }
    case TableColumnType.ANTIBODY_DOMAIN: {
      if (formattedValue === '' || typedValue.type === BaseTypedValueType.BIOENTITY) {
        return {
          resolvedValue: typedValue,
          error: null,
        };
      }
      return resolveTypedValueToAntibodyDomain({
        resolveFromResolutionQuery,
        typedValue,
        formattedValue,
        columnDefinition,
      });
    }
    // POSITION, QUANTITY, and BASES types are resolved via customEval
    default:
      return {resolvedValue: typedValue, error: null};
  }
}

export function resolveTypedValueWithRedux(
  columnDefinition: ColumnDefinition,
  typedValue: TypedValue,
  options: Partial<ResolutionContext> = {}
): ResolvedValueInfo {
  return resolveTypedValue(
    _resolveFromResolutionQueryNoWait,
    columnDefinition,
    typedValue,
    null /* languageTag */,
    options
  );
}
